/**
 * 幂等键 — §6.0-⑥ 冻结口径：`Idempotency-Key: <uuid>`，**值必须是合法 UUID**。
 *
 * seq322 §五 裁定后的四态口径（v1.8 权威）：
 * | 情形 | 返回 | 前端处置 |
 * |---|---|---|
 * | 同键 + 同 payload（弱网重试、重复点击） | 200 `OK` + **原结果**（含原 `orderNo`） | 透明重放：直接消费返回体，**不需要二次查询** |
 * | 同键 + 不同 payload | 409 `IDEMPOTENCY_KEY_REUSED` | 显式拒绝并提示核对内容；**禁止换新键重录** |
 * | 无键 | 409 `IDEMPOTENCY_KEY_MISSING` | 判为前端封装缺陷，不提示用户 |
 * | 非 UUID | 400 `IDEMPOTENCY_KEY_INVALID` | 判为前端封装缺陷，不提示用户 |
 *
 * 第一行正是本模块「同一业务动作重试必须复用同一键」的理由：只有键不变，重试才拿得回原
 * `orderNo`；若此处退化成 409 而前端换新键重录，就直接造出重复订单。故键持久化到
 * sessionStorage，**失败保留、成功才释放**。`scope` 只用于本地存储键与人工排查，
 * **不进请求头值**（UUID 格式是硬约束）。
 *
 * **S-13 现状（seq340）**：上述复用只在服务端已有幂等登记表时才成立。当前部署只做
 * 「存在性 + UUID 格式」校验，无登记表、无结果缓存 ⇒ 同键重放**不会**拿回原结果，
 * 而是再执行一次。所以现阶段防重复提交不依赖本模块，而依赖两道真实防线：
 * http 层写操作一律不自动重试（`client.ts` `shouldRetry`）+ 视图层一次业务意图只允许
 * 一条在途写。**S-13 落地后本模块无需改动**，两道防线也继续保留（重复点击仍不该打两次）。
 */

const PREFIX = 'idem';

/** 合法 UUID v4：优先 Web Crypto；非安全上下文（如 http://192.168.66.48:5173）无 randomUUID 时手写 */
export function newUuid(): string {
  const c = typeof crypto === 'undefined' ? undefined : crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 形状校验：仅用于自检与测试断言，不在热路径上跑正则 */
export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

/** 新生成一个幂等键（合法 UUID）。`scope` 保留为形参以兼容调用点，不参与取值。 */
export function newIdempotencyKey(scope?: string): string {
  void scope;
  return newUuid();
}

/**
 * 取得某业务动作的稳定幂等键：已存在则复用（重试语义），否则生成并持久化。
 * @param scope 业务域，如 'order-proxy'
 * @param bizKey 业务唯一标识，如表单草稿 id 或 手机号+意向产品
 */
export function stableIdempotencyKey(scope: string, bizKey: string): string {
  const storageKey = `${PREFIX}:${scope}:${bizKey}`;
  try {
    const hit = window.sessionStorage.getItem(storageKey);
    if (hit) return hit;
    const created = newUuid();
    window.sessionStorage.setItem(storageKey, created);
    return created;
  } catch {
    // 隐私模式/存储不可用时退化为一次性键
    return newUuid();
  }
}

/** 提交成功后释放键，避免下次同业务动作复用旧键 */
export function releaseIdempotencyKey(scope: string, bizKey: string): void {
  try {
    window.sessionStorage.removeItem(`${PREFIX}:${scope}:${bizKey}`);
  } catch {
    /* 忽略存储异常 */
  }
}

/** 幂等提交包装：失败保留键以便重试，成功释放 */
export async function withIdempotency<T>(
  scope: string,
  bizKey: string,
  action: (key: string) => Promise<T>,
): Promise<T> {
  const key = stableIdempotencyKey(scope, bizKey);
  const result = await action(key);
  releaseIdempotencyKey(scope, bizKey);
  return result;
}
